import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
  maxValueValidation,
  minLengthValidation,
  minValueValidation,
} from '../../utils/validations.util';
import SnackbarInfo from '../common/snackbar-info.component';
import Channels from '../form-elements/channels.component';
import {
  updateDataById,
  createNewData,
} from '../../services/distributor.service';
import replaceIdUrl from '../../utils/url.util';
import ROUTE_PATH from '../../config/routes.config';
import Status from '../form-elements/status.component';
import TimeZone from '../form-elements/timezone.component';
import ContentOwnerRoles from '../form-elements/content-owner-roles.component';
import CONTENT_OWNER_LIST from '../../config/content-owner-roles.config';
import TIMEZONE_LIST from '../../config/time-zone.config';
import Countries from '../form-elements/countries.component';
import BankPreference from '../form-elements/bank-preference.component';

const ContentOwnerBasicInfo = ({
  distributorDetail,
  handleRefreshData,
  formValueChangedCallback,
  editMode,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      username: '',
      firstName: '',
      lastName: '',
      website: '',
      type: '',
      royality: null,
      streetAddress: '',
      city: '',
      state: '',
      zipcode: '',
      country: '',
      email: '',
      phone: '',
      bank1: '',
      bankAcc1: '',
      bank2: '',
      bankAcc2: '',
      bankPreference: 'bank1',
      rawBankInfo: '',
      canEditLicense: 0,
      timezone: {
        id: 'GMT',
        title: 'GMT Greenwich Mean Time (GMT)',
      },
      info: '',
      status: 1,
      channels: [],
    },
  });

  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createNewData(payload)
      .then((res) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Distributor created successfully.',
        });
        formValueChangedCallback(false);
        setFormSubmitted(false);
        const navigateUrl = replaceIdUrl(
          ROUTE_PATH.CONTENT_OWNERS_EDIT,
          res.data.id
        );
        navigate(navigateUrl);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(distributorDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Distributor details updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      username: data.username,
      type: typeof data.type === 'object' ? data.type.id : data.type,
      timezone:
        typeof data.timezone === 'object' ? data.timezone.id : data.timezone,
      royality: data.royality,
      country: data.country,
      phone: data.phone,
      bank1: data.bank1,
      bankAcc1: data.bankAcc1,
      bank2: data.bank2,
      bankAcc2: data.bankAcc2,
      bankPreference: data.bankPreference,
      rawBankInfo: data.rawBankInfo,
      website: data.website,
      streetAddress: data.streetAddress,
      city: data.city,
      state: data.state,
      zipcode: data.zipcode,
      info: data.info,
      canEditLicense: Number(data.canEditLicense),
      channels: data.channels,
      status: Number(data.status),
    };
    if (editMode) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!editMode) {
      return;
    }

    const type = {
      id: distributorDetail.type || '',
      title:
        CONTENT_OWNER_LIST.filter(
          (item) => item.id === distributorDetail.type
        )?.[0]?.title || '',
    };
    const timezone = {
      id: distributorDetail.timezone || 'GMT',
      title:
        TIMEZONE_LIST.filter(
          (item) => item.id === distributorDetail.timezone
        )?.[0]?.title || 'GMT Greenwich Mean Time (GMT)',
    };

    setValue('firstName', distributorDetail.firstName || '');
    setValue('lastName', distributorDetail.lastName || '');
    setValue('email', distributorDetail.email || '');
    setValue('username', distributorDetail.username || '');
    setValue('status', Number(distributorDetail.status || 0));
    setValue('type', type);
    setValue('timezone', timezone);
    setValue('royality', Number(distributorDetail.royality || ''));
    setValue('country', distributorDetail.country || '');
    setValue('phone', distributorDetail.phone || '');
    setValue('bank1', distributorDetail.bank1 || '');
    setValue('bankAcc1', distributorDetail.bankAcc1 || '');
    setValue('bank2', distributorDetail.bank2 || '');
    setValue('bankAcc2', distributorDetail.bankAcc2 || '');
    setValue('bankPreference', distributorDetail.bankPreference || '');
    setValue('rawBankInfo', distributorDetail.rawBankInfo || '');
    setValue('website', distributorDetail.website || '');
    setValue('streetAddress', distributorDetail.streetAddress || '');
    setValue('city', distributorDetail.city || '');
    setValue('state', distributorDetail.state || '');
    setValue('zipcode', distributorDetail.zipcode || '');
    setValue('info', distributorDetail.info || '');
    setValue('canEditLicense', Number(distributorDetail.canEditLicense || 0));
    setValue('channels', distributorDetail.channels || []);
  }, [distributorDetail.id]);

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form
        id="content-owner-basic-info-form"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="middle"
              sx={{
                fontWeight: 'bold',
                py: 1,
              }}
            >
              Profile
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="username"
              control={control}
              rules={{
                ...validationRules.REQUIRED,
                ...minLengthValidation(3),
                ...maxLengthValidation(100),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name/Company *"
                  type="text"
                  error={!!errors.username}
                  helperText={errors?.username?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="firstName"
              control={control}
              rules={{
                ...validationRules.REQUIRED,
                ...maxLengthValidation(32),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name *"
                  type="text"
                  error={!!errors.firstName}
                  helperText={errors?.firstName?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="lastName"
              control={control}
              rules={{
                ...validationRules.REQUIRED,
                ...maxLengthValidation(32),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name *"
                  type="text"
                  error={!!errors.lastName}
                  helperText={errors?.lastName?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="website"
              control={control}
              rules={validationRules.OPTIONAL_URL}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Website"
                  error={!!errors.website}
                  helperText={errors?.website?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="type"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <ContentOwnerRoles
                  id="content_owner_type"
                  name="content_owner_type"
                  label="Type *"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={[0, 1, 2]}
                  sx={{ width: '100%' }}
                  error={errors?.type?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="royality"
              control={control}
              rules={{
                ...validationRules.REQUIRED,
                ...minValueValidation(0),
                ...maxValueValidation(100),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Royality *"
                  type="number"
                  error={!!errors.royality}
                  helperText={errors?.royality?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="middle"
              sx={{
                fontWeight: 'bold',
                py: 1,
              }}
            >
              Address
            </Divider>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="streetAddress"
              control={control}
              rules={maxLengthValidation(1000)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Street Address"
                  error={!!errors.streetAddress}
                  helperText={errors?.streetAddress?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="city"
              control={control}
              rules={maxLengthValidation(100)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="City"
                  type="text"
                  error={!!errors.city}
                  helperText={errors?.city?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="state"
              control={control}
              rules={maxLengthValidation(100)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="State"
                  type="text"
                  error={!!errors.state}
                  helperText={errors?.state?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="zipcode"
              control={control}
              rules={maxLengthValidation(32)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Zipcode"
                  error={!!errors.zipcode}
                  helperText={errors?.zipcode?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="country"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Countries
                  id="country"
                  name="country"
                  label="Country"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.country?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="email"
              control={control}
              rules={{
                ...maxLengthValidation(255),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email *"
                  type="text"
                  error={!!errors.email}
                  helperText={errors?.email?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="phone"
              control={control}
              rules={maxLengthValidation(128)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone"
                  type="tel"
                  error={!!errors.phone}
                  helperText={errors?.phone?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="middle"
              sx={{
                fontWeight: 'bold',
                py: 1,
              }}
            >
              Payment
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="bank1"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank 1"
                  error={!!errors.bank1}
                  helperText={errors?.bank1?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="bankAcc1"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank Account 1"
                  error={!!errors.bankAcc1}
                  helperText={errors?.bankAcc1?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="bank2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank 2"
                  error={!!errors.bank2}
                  helperText={errors?.bank2?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="bankAcc2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bank Account 2"
                  error={!!errors.bankAcc2}
                  helperText={errors?.bankAcc2?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="bankPreference"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <BankPreference
                  id="bank_preference"
                  name="bank_preference"
                  label="Bank Preference"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.bankPreference?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="rawBankInfo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unprocessed Bank Info"
                  error={!!errors.rawBankInfo}
                  helperText={errors?.rawBankInfo?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="middle"
              sx={{
                fontWeight: 'bold',
                py: 1,
              }}
            >
              Other
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="canEditLicense"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="filled"
                  error={!!errors.canEditLicense}
                >
                  <InputLabel>Can Edit License</InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? 0}
                    onChange={field.onChange}
                  >
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Yes</MenuItem>
                  </Select>
                  {errors?.canEditLicense && (
                    <FormHelperText>
                      {errors.canEditLicense.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="timezone"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TimeZone
                  id="timezone"
                  name="timezone"
                  label="Time zone:"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.timezone?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="info"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  error={!!errors.info}
                  helperText={errors?.info?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="status"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <Status
                  id="user_status"
                  name="user_status"
                  label="Status"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={[0, 1]}
                  sx={{ width: '100%' }}
                  error={errors?.status?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select channel(s) if distributor has specific channels as
              preferences to publish content to. Leave empty if no preferences.
            </Typography>
            <Controller
              name="channels"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Channels
                  id="user_channels"
                  name="user_channels"
                  label="Select Channels"
                  defaultValue={value}
                  multiple
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.channels?.message || ''}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="content-owner-basic-info-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Basic Info
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

ContentOwnerBasicInfo.propTypes = {
  distributorDetail: PropTypes.object.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default ContentOwnerBasicInfo;
